import { z } from 'zod';
import { AttentionState, type AttentionOwner } from '../enums';
import type { AttentionTemperature } from '../funnel/attention';

/** Estado de atención de un lead sin cita (null = el bot no lo atiende / ya salió del tablero). */
export interface LeadAttention {
  owner: AttentionOwner;
  state: AttentionState;
  /** Mensajes enviados sin respuesta del cliente. */
  attempts: number;
  /** Último mensaje del bot. */
  lastAt: string | null;
  /** Próximo intento programado por el bot. */
  nextAt: string | null;
  /** Desde cuándo está en pausa por un humano (owner = HUMAN). */
  pausedAt: string | null;
  pausedById: string | null;
  /** Última nota del bot (motivo de prioridad, de descarte…). */
  note: string | null;
}

/** El bot actualiza el estado de atención (`PATCH /leads/:id/attention`). */
export const attentionUpdateSchema = z.object({
  state: z.nativeEnum(AttentionState),
  attempts: z.number().int().min(0).max(99).optional(),
  lastAt: z.string().datetime().optional().nullable(),
  nextAt: z.string().datetime().optional().nullable(),
  note: z.string().trim().max(500).optional().nullable(),
});
export type AttentionUpdateInput = z.infer<typeof attentionUpdateSchema>;

/** Un humano toma o devuelve la conversación (`POST /leads/:id/attention/take | release`). */
export const attentionNoteSchema = z.object({
  note: z.string().trim().max(500).optional().nullable(),
});

export interface AttentionResponse {
  leadId: string;
  attention: LeadAttention | null;
  /** Límites de la empresa que el bot debe respetar. */
  limits: {
    maxAttempts: number;
    attemptWindowHours: number;
    quietFrom: string | null;
    quietTo: string | null;
    resumeAfterMinutes: number;
  };
}

/** Tablero "Atención del bot" (`GET /attention/board`): leads sin cita que atiende (o atendió) el bot, por estado. */
export const attentionBoardQuerySchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  advisorId: z.string().uuid().optional(),
  /** Tarjetas por columna. */
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type AttentionBoardQuery = z.infer<typeof attentionBoardQuerySchema>;

export interface AttentionCard {
  leadId: string;
  contactName: string;
  phone: string | null;
  projectId: string;
  projectName: string;
  advisorId: string | null;
  advisorName: string | null;
  score: number;
  temperature: AttentionTemperature;
  capturedAt: string;
  attention: LeadAttention;
  /** Quién pausó al bot (dueño HUMAN). */
  pausedByName: string | null;
  /** Último mensaje de la conversación de WhatsApp que el bot guardó en el CRM. */
  lastMessage: { text: string | null; direction: 'INBOUND' | 'OUTBOUND'; sentAt: string } | null;
}

/**
 * ¿El bot de la empresa puede atender leads de verdad? Hace falta el interruptor encendido (Administración → Bot
 * de atención), un número de WhatsApp y la plantilla de primer contacto. Sin eso, «el bot escribe primero» no
 * aplica: el lead nace con la tarea «Primer contacto» y el bot no retoma (`GET /attention/readiness`).
 */
export interface BotReadiness {
  ready: boolean;
  active: boolean;
  hasNumber: boolean;
  hasTemplate: boolean;
  /** Primer motivo por el que no está listo (código estable); null si está listo. */
  code: BotNotReadyCode | null;
  /** Ese motivo en lenguaje claro, con la pantalla donde se arregla; null si está listo. */
  reason: string | null;
}

/** Por qué el bot de una empresa no puede atender: apagado, sin número de WhatsApp o sin plantilla de primer contacto. */
export type BotNotReadyCode = 'INACTIVE' | 'NO_NUMBER' | 'NO_TEMPLATE';

export interface AttentionBoard {
  companyId: string;
  /** Estado del bot de la empresa, para avisar cuando el tablero muestra leads que nadie atiende. */
  bot: BotReadiness;
  columns: Array<{ state: AttentionState; count: number; cards: AttentionCard[] }>;
  /** Leads del tablero cuyo bot está en pausa por un asesor. */
  pausedCount: number;
  total: number;
  generatedAt: string;
}
